// Owner Analytics — super_admin date-range order analysis (John & Priya).
// Slice 1: range picker + order funnel + daily trend chart.
// Slices 2-6 (vendor, product, exceptions, delivery) land on this page later.

import { useEffect, useState } from 'react';
import { cockpitApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  Loader2, RefreshCcw, Inbox, Send, Truck, PackageCheck, XCircle,
  ArrowUp, ArrowDown, Minus, Store, Package, Search,
  AlertTriangle, Repeat, Banknote,
} from 'lucide-react';

function iso(d) { return d.toISOString().slice(0, 10); }
function todayIso() { return iso(new Date()); }
function daysAgoIso(n) { return iso(new Date(Date.now() - n * 86_400_000)); }

const PRESETS = [
  { key: '7d',  label: 'Last 7 days',  range: () => ({ from: daysAgoIso(6),  to: todayIso() }) },
  { key: '30d', label: 'Last 30 days', range: () => ({ from: daysAgoIso(29), to: todayIso() }) },
  { key: 'month', label: 'This month', range: () => ({ from: `${todayIso().slice(0, 7)}-01`, to: todayIso() }) },
  { key: 'lastMonth', label: 'Last month', range: () => {
      const d = new Date(); d.setDate(1); d.setDate(0); // last day of prev month
      return { from: `${iso(d).slice(0, 7)}-01`, to: iso(d) };
    } },
];

function fmtNum(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN');
}

function fmtInr(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000)    return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

const STAGES = [
  { key: 'received',   label: 'Received',   Icon: Inbox,       color: 'bg-blue-500',    chip: 'bg-blue-50 text-blue-700',
    info: 'Orders that arrived in Dispatch (uploaded from MBO) within the selected dates. A split order counts each part separately.' },
  { key: 'dispatched', label: 'Dispatched', Icon: Send,        color: 'bg-indigo-500',  chip: 'bg-indigo-50 text-indigo-700', avgKey: 'intakeToDispatch',  avgLabel: 'from received',
    info: 'Orders whose dispatch file was emailed to the vendor within the selected dates. The "avg days" below it is the average wait from an order arriving to it being dispatched.' },
  { key: 'shipped',    label: 'Shipped',    Icon: Truck,       color: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700', avgKey: 'dispatchToShipped', avgLabel: 'from dispatch',
    info: 'Orders whose India Post tracking number was recorded within the selected dates — i.e. the vendor actually handed the parcel over. "Avg days" is how long vendors took after receiving the dispatch email.' },
  { key: 'delivered',  label: 'Delivered',  Icon: PackageCheck, color: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700', avgKey: 'shippedToDelivered', avgLabel: 'from shipped',
    info: 'Orders India Post marked as delivered within the selected dates. "Avg days" is the transit time from tracking number to delivery.' },
];

function Funnel({ funnel, avgDays }) {
  const base = Math.max(funnel.received, funnel.dispatched, funnel.shipped, funnel.delivered, 1);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">
        Order funnel{' '}
        <InfoTip title="Order funnel" align="right">
          How orders moved through the pipeline in your selected dates. Each
          number counts orders whose event <b>happened inside the range</b> —
          so "Delivered" can include orders received before the range, and
          "Shipped" can exceed "Received" when older orders catch up. The %
          compares each stage to orders received in the same range.
        </InfoTip>
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {STAGES.map((s) => {
          const n = funnel[s.key];
          const pctOfReceived = funnel.received > 0 ? Math.round((n / funnel.received) * 100) : null;
          const avg = s.avgKey ? avgDays[s.avgKey] : null;
          return (
            <div key={s.key} className="rounded-lg border border-gray-100 p-3 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${s.chip}`}>
                  <s.Icon className="w-4 h-4" />
                </span>
                <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">{s.label}</span>
                {s.info && <InfoTip title={s.label} align={s.key === 'received' ? 'right' : 'center'}>{s.info}</InfoTip>}
              </div>
              <div className="text-2xl font-semibold text-gray-900 tabular-nums">{fmtNum(n)}</div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${s.color}`} style={{ width: `${Math.round((n / base) * 100)}%` }} />
              </div>
              <div className="text-xs text-gray-500 leading-snug">
                {s.key === 'received'
                  ? 'orders that came in'
                  : <>{pctOfReceived != null && <>{pctOfReceived}% of received</>}</>}
                {avg != null && (
                  <div className="text-gray-600 mt-0.5">avg <b>{avg}</b> days {s.avgLabel}</div>
                )}
              </div>
            </div>
          );
        })}

        <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-rose-50 text-rose-700">
              <XCircle className="w-4 h-4" />
            </span>
            <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">Cancelled</span>
            <InfoTip title="Cancelled" align="left">
              Orders moved to the cancelled stage within the selected dates,
              whatever the reason — customer cancelled, payment declined,
              out of stock with no substitute, and so on.
            </InfoTip>
          </div>
          <div className="text-2xl font-semibold text-rose-700 tabular-nums">{fmtNum(funnel.cancelled)}</div>
          <div className="text-xs text-gray-500 leading-snug">cancelled in this period</div>
        </div>
      </div>
    </div>
  );
}

const SERIES = [
  { key: 'received',   label: 'Received',   stroke: '#3b82f6' },
  { key: 'dispatched', label: 'Dispatched', stroke: '#6366f1' },
  { key: 'delivered',  label: 'Delivered',  stroke: '#10b981' },
];

function TrendChart({ trend }) {
  const W = 920, H = 240, PAD = { l: 36, r: 12, t: 12, b: 28 };
  const innerW = W - PAD.l - PAD.r, innerH = H - PAD.t - PAD.b;
  const maxY = Math.max(4, ...trend.flatMap((d) => SERIES.map((s) => d[s.key])));
  const n = trend.length;
  const x = (i) => PAD.l + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v) => PAD.t + innerH - (v / maxY) * innerH;

  const [hover, setHover] = useState(null); // index of hovered day

  function onMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    if (n <= 1) { setHover(n === 1 ? 0 : null); return; }
    const i = Math.round(((svgX - PAD.l) / innerW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  }

  const yTicks = [];
  const step = Math.max(1, Math.ceil(maxY / 4));
  for (let v = 0; v <= maxY; v += step) yTicks.push(v);

  const labelEvery = Math.max(1, Math.ceil(n / 8));

  const hd = hover != null ? trend[hover] : null;
  // Keep the tooltip inside the chart: flip to the left of the guide line
  // once the cursor passes the middle.
  const tipW = 150;
  const tipX = hd ? (x(hover) > W / 2 ? x(hover) - tipW - 12 : x(hover) + 12) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-900">
          Daily flow{' '}
          <InfoTip title="Daily flow" align="right">
            Orders received, dispatched and delivered on each calendar day of
            your selected range. The three lines should roughly track each
            other — a day where the dispatched line drops while received stays
            up means a backlog formed that day. Hover anywhere on the chart
            for exact numbers.
          </InfoTip>
        </h2>
        <div className="flex items-center gap-4">
          {SERIES.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.stroke }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[640px]" role="img"
             onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="#f3f4f6" />
              <text x={PAD.l - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="#9ca3af">{v}</text>
            </g>
          ))}
          {trend.map((d, i) => (i % labelEvery === 0 || i === n - 1) && (
            <text key={d.day} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#9ca3af">
              {format(new Date(d.day), 'dd MMM')}
            </text>
          ))}
          {SERIES.map((s) => (
            <polyline key={s.key} fill="none" stroke={s.stroke} strokeWidth="2"
              points={trend.map((d, i) => `${x(i)},${y(d[s.key])}`).join(' ')} />
          ))}
          {SERIES.map((s) => trend.map((d, i) => (
            <circle key={`${s.key}-${d.day}`} cx={x(i)} cy={y(d[s.key])}
                    r={hover === i ? 4 : 2.5} fill={s.stroke}
                    stroke={hover === i ? '#fff' : 'none'} strokeWidth="1.5" />
          )))}

          {hd && (
            <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={PAD.t + innerH}
                    stroke="#9ca3af" strokeDasharray="3 3" />
              <rect x={tipX} y={PAD.t + 4} width={tipW} height={SERIES.length * 18 + 26}
                    rx="8" fill="#111827" opacity="0.92" />
              <text x={tipX + 12} y={PAD.t + 22} fontSize="11" fontWeight="600" fill="#fff">
                {format(new Date(hd.day), 'EEE dd MMM')}
              </text>
              {SERIES.map((s, si) => (
                <g key={s.key}>
                  <circle cx={tipX + 16} cy={PAD.t + 36 + si * 18} r="3.5" fill={s.stroke} />
                  <text x={tipX + 26} y={PAD.t + 40 + si * 18} fontSize="11" fill="#e5e7eb">
                    {s.label}
                  </text>
                  <text x={tipX + tipW - 12} y={PAD.t + 40 + si * 18} fontSize="11"
                        fontWeight="600" fill="#fff" textAnchor="end">
                    {fmtNum(hd[s.key])}
                  </text>
                </g>
              ))}
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}

function PrevDelta({ now, prev }) {
  if (now == null || prev == null || prev === 0) {
    return <span className="text-[11px] text-gray-400">—</span>;
  }
  const diff = now - prev;
  if (diff === 0) {
    return <span className="inline-flex items-center gap-1 text-[11px] text-gray-500"><Minus className="w-3 h-3" /> flat</span>;
  }
  const up = diff > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  const cls = up ? 'text-emerald-600' : 'text-rose-600';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {Math.abs(Math.round((diff / prev) * 100))}%
    </span>
  );
}

function VendorTable({ vendorPerf }) {
  const { vendors, prevPeriod } = vendorPerf;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Store className="w-4 h-4 text-indigo-600" />
        Vendor performance{' '}
        <InfoTip title="Vendor performance" align="right">
          One row per vendor with orders dispatched in your selected dates.
          <br />• <b>vs prev</b> — order volume compared to the period of the
          same length just before ({prevPeriod.from} onwards).
          <br />• <b>Fill rate</b> — orders minus out-of-stock responses, as a
          %. Red below 95%.
          <br />• <b>OOS</b> — out-of-stock (and similar can't-supply)
          responses in the range.
          <br />• <b>Avg ship days</b> — dispatch email → tracking number
          received. Red above 3 days.
          <br />• <b>₹ payable</b> — quantity × the price effective on each
          dispatch date, plus shipping.
          <br />• <b>Unpriced</b> — dispatched lines with no price on file;
          these make the payable understated.
        </InfoTip>
      </h2>
      {vendors.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center">
          No vendor dispatches in this range.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-right px-3 py-2">Orders</th>
                <th className="text-right px-3 py-2">vs prev</th>
                <th className="text-right px-3 py-2">Fill rate</th>
                <th className="text-right px-3 py-2">OOS</th>
                <th className="text-right px-3 py-2">Avg ship days</th>
                <th className="text-right px-3 py-2">Delivered</th>
                <th className="text-right px-3 py-2">₹ payable</th>
                <th className="text-right px-3 py-2">Unpriced</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vendors.map((v) => (
                <tr key={v.vendorId}>
                  <td className="px-3 py-2 font-medium">{v.vendorName}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(v.orders)}</td>
                  <td className="px-3 py-2 text-right"><PrevDelta now={v.orders} prev={v.prevOrders} /></td>
                  <td className={`px-3 py-2 text-right tabular-nums ${v.fillRatePct != null && v.fillRatePct < 95 ? 'text-rose-700 font-semibold' : ''}`}>
                    {v.fillRatePct != null ? `${v.fillRatePct}%` : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${v.oos > 10 ? 'text-rose-700 font-semibold' : ''}`}>{fmtNum(v.oos)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${v.avgShipDays != null && v.avgShipDays > 3 ? 'text-rose-700 font-semibold' : ''}`}>
                    {v.avgShipDays != null ? `${v.avgShipDays}d` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(v.delivered)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtInr(v.inrTotalDue)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${v.unpricedLines > 0 ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>
                    {v.unpricedLines > 0 ? v.unpricedLines : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PRODUCT_PAGE = 25;

function ProductTable({ productPerf }) {
  const { totals, products } = productPerf;
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [unpricedOnly, setUnpricedOnly] = useState(false);

  const filtered = products.filter((p) =>
    (!unpricedOnly || p.unpricedUnits > 0) &&
    (!search || p.productName.toLowerCase().includes(search.toLowerCase()))
  );
  const visible = showAll ? filtered : filtered.slice(0, PRODUCT_PAGE);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Package className="w-4 h-4 text-violet-600" />
          Product analysis{' '}
          <InfoTip title="Product analysis" align="right">
            Every product dispatched to a vendor in your selected dates,
            biggest movers first. Built from the same line items as Vendor
            Payables, so the ₹ figures always agree.
            <br />• <b>Units</b> — total quantity dispatched.
            <br />• <b>₹/unit</b> — average price actually billed (the price
            effective on each dispatch date).
            <br />• <b>₹ line cost</b> and <b>% of spend</b> — what this
            product contributes to the period's vendor bill.
            <br />• <b>Unpriced units</b> — dispatched but missing a price;
            they add ₹0 until Kalpana prices them.
          </InfoTip>
        </h2>
        <span className="text-xs text-gray-500">
          {fmtNum(totals.distinctProducts)} products · {fmtNum(totals.totalUnits)} units
          {totals.unpricedProducts > 0 && (
            <span className="text-amber-700">
              {' '}· {fmtNum(totals.unpricedProducts)} product(s) with unpriced units
            </span>
          )}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={unpricedOnly}
                   onChange={(e) => setUnpricedOnly(e.target.checked)}
                   className="rounded border-gray-300" />
            Unpriced only
          </label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search product…"
                   className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-48" />
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center">No matching products.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-right px-3 py-2">Orders</th>
                <th className="text-right px-3 py-2">Units</th>
                <th className="text-right px-3 py-2">₹/unit</th>
                <th className="text-right px-3 py-2">₹ line cost</th>
                <th className="text-right px-3 py-2">% of spend</th>
                <th className="text-right px-3 py-2">Unpriced units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((p) => (
                <tr key={p.productName} className={p.unpricedUnits > 0 ? 'bg-amber-50/40' : ''}>
                  <td className="px-3 py-2 font-medium">{p.productName}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{p.vendors.join(', ')}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.orders)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.units)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {p.avgUnitPrice != null ? `₹${fmtNum(p.avgUnitPrice)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtInr(p.lineCost)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                    {p.pctOfLineCost != null ? `${p.pctOfLineCost}%` : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${p.unpricedUnits > 0 ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>
                    {p.unpricedUnits > 0 ? fmtNum(p.unpricedUnits) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > PRODUCT_PAGE && (
        <button onClick={() => setShowAll((v) => !v)}
                className="mt-3 text-sm text-blue-600 hover:underline">
          {showAll ? 'Show top 25 only' : `Show all ${fmtNum(filtered.length)} products`}
        </button>
      )}
    </div>
  );
}

const EVENT_LABELS = {
  order_cancelled: 'Order cancelled',
  hold_requested: 'Put on hold',
  hold_released: 'Hold released',
  vendor_oos_need_alt: 'Vendor out of stock',
  customer_clarified_alt: 'Customer accepted alternative',
  customer_refused_alt: 'Customer refused alternative',
  address_correction_approved: 'Address correction approved',
  reship_needs_linkage: 'Reship awaiting link',
  reship_linked: 'Reship linked',
  virtual_clarified: 'Virtual product clarified',
  manual_substitute: 'Manual substitute',
  substitute_approved: 'Substitute approved',
  vendor_delayed: 'Vendor delayed',
  vendor_wrong_product: 'Wrong product shipped',
  partial_shipment_recorded: 'Partial shipment',
  refund_marked_paid: 'Refund paid',
};

const RESHIP_LABELS = {
  customs_hold: 'Stuck in customs',
  lost: 'Lost in transit',
  damaged: 'Damaged parcel',
  missing_pills: 'Missing pills',
  wrong_product: 'Wrong product',
  customer_feedback: 'Customer dissatisfied',
  delivered_want_another: 'Delivered — wants another',
  delivered_not_received: 'Delivered, customer says not received',
  other: 'Other',
  no_reason_recorded: '⚠ No reason recorded',
};

const REFUND_LABELS = {
  refund_pending: 'Refund pending',
  refund_processed: 'Refund processed',
  payment_link_sent: 'Payment link sent',
  payment_collected: 'Payment collected',
  payment_declined: 'Payment declined',
  no_response: 'No response from customer',
};

function BarList({ rows, labels, total, emptyText, warnKey }) {
  if (!rows || rows.length === 0) {
    return <div className="py-5 text-center text-xs text-gray-400">{emptyText}</div>;
  }
  const max = Math.max(...rows.map((r) => r.n), 1);
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => {
        const key = r.reason_code || r.reason || r.status;
        const warn = warnKey && key === warnKey;
        return (
          <li key={key}>
            <div className="flex items-baseline justify-between text-xs mb-0.5">
              <span className={warn ? 'text-rose-700 font-medium' : 'text-gray-700'}>
                {labels[key] || key.replace(/_/g, ' ')}
              </span>
              <span className="font-semibold tabular-nums">
                {fmtNum(r.n)}
                {total > 0 && <span className="text-gray-400 font-normal"> · {Math.round((r.n / total) * 100)}%</span>}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${warn ? 'bg-rose-400' : 'bg-gray-400'}`}
                   style={{ width: `${Math.max(3, Math.round((r.n / max) * 100))}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ExceptionsSection({ exceptions }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          Exception events
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">{fmtNum(exceptions.eventsTotal)}</span>
          <InfoTip title="Exception events">
            Every significant out-of-the-ordinary moment recorded in the selected
            dates — cancellations, holds, out-of-stock responses, substitutes,
            wrong/partial shipments, refunds. Counted from the order handoff
            history, so each entry has a who/when behind it (see Recent events on
            the cockpit for the individual stories). Routine steps are excluded.
          </InfoTip>
        </h2>
        <BarList rows={exceptions.events} labels={EVENT_LABELS}
                 total={exceptions.eventsTotal}
                 emptyText="No exception events in this period — clean run." />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Repeat className="w-4 h-4 text-violet-600" />
          Reships by reason
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-800">{fmtNum(exceptions.reshipsTotal)}</span>
          <InfoTip title="Reships by reason">
            Reship orders that arrived in the selected dates, grouped by why the
            reship happened. The reason drives what the vendor charges us — lost,
            damaged, customs and missing-pills reships bill at 50%; the rest at
            full price. "No reason recorded" rows bill at 100% until Kalpana sets
            the reason (Data backfill page).
          </InfoTip>
        </h2>
        <BarList rows={exceptions.reships} labels={RESHIP_LABELS}
                 total={exceptions.reshipsTotal} warnKey="no_reason_recorded"
                 emptyText="No reships created in this period." />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Banknote className="w-4 h-4 text-emerald-600" />
          Refunds & payments
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">{fmtNum(exceptions.refundsTotal)}</span>
          <InfoTip title="Refunds & payments">
            Orders whose refund/payment status changed in the selected dates —
            the money tail of cancellations and delivered-unpaid orders. Status
            only; the actual money moves outside DMS. Every change here is
            audit-logged with who made it.
          </InfoTip>
        </h2>
        <BarList rows={exceptions.refunds} labels={REFUND_LABELS}
                 total={exceptions.refundsTotal}
                 emptyText="No refund / payment movement in this period." />
      </div>
    </div>
  );
}

const TRANSIT_BUCKETS = [
  ['lt_7', 'Under 7 days', 'bg-emerald-500'],
  ['d7_14', '7–14 days', 'bg-blue-500'],
  ['d14_21', '14–21 days', 'bg-amber-500'],
  ['gt_21', 'Over 21 days', 'bg-rose-500'],
];

function DeliverySection({ delivery }) {
  const s = delivery.summary;
  const bucketTotal = Object.values(delivery.transitBuckets).reduce((a, b) => a + b, 0);

  const Stat = ({ label, value, sub, alert }) => (
    <div className="rounded-lg border border-gray-100 p-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${alert ? 'text-rose-700' : 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
      <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
        <Truck className="w-4 h-4 text-blue-600" />
        Delivery performance
        <InfoTip title="Delivery performance">
          Follows the parcels that were <b>shipped in your selected dates</b>
          (tracking number recorded) through to their outcome — delivered,
          still moving, or returned — even if the outcome happened after the
          range. Transit time = tracking number recorded → India Post says
          delivered. "Stuck &gt; 14 days" parcels are still moving and need
          chasing with India Post.
        </InfoTip>
      </h2>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Parcels shipped" value={fmtNum(s.shipped)} />
        <Stat label="Delivered" value={s.deliveredPct != null ? `${s.deliveredPct}%` : '—'}
              sub={`${fmtNum(s.delivered)} parcel(s)`} />
        <Stat label="Avg transit" value={s.avgTransitDays != null ? `${s.avgTransitDays}d` : '—'}
              sub={s.maxTransitDays != null ? `slowest ${s.maxTransitDays}d` : null} />
        <Stat label="Still moving" value={fmtNum(s.stillMoving)} />
        <Stat label="Stuck > 14 days" value={fmtNum(s.stuckGt14d)} alert={s.stuckGt14d > 0} />
        <Stat label="Returned / undelivered" value={fmtNum(s.returnedUndelivered)} alert={s.returnedUndelivered > 0} />
      </div>

      {bucketTotal > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            How long deliveries took
            <InfoTip title="How long deliveries took">
              Distribution of transit times for the delivered parcels in this
              cohort. A growing red slice means India Post is slowing down —
              compare different date ranges to spot the trend.
            </InfoTip>
          </div>
          <div className="flex h-4 rounded-full overflow-hidden border border-gray-200">
            {TRANSIT_BUCKETS.map(([key, , color]) => {
              const n = delivery.transitBuckets[key];
              if (!n) return null;
              return <div key={key} className={color} style={{ width: `${(n / bucketTotal) * 100}%` }}
                          title={`${n} parcel(s)`} />;
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-2">
            {TRANSIT_BUCKETS.map(([key, label, color]) => (
              <span key={key} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
                {label} · <b>{fmtNum(delivery.transitBuckets[key])}</b>
                {bucketTotal > 0 && <span className="text-gray-400">({Math.round((delivery.transitBuckets[key] / bucketTotal) * 100)}%)</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {delivery.byCountry.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Destination</th>
                <th className="text-right px-3 py-2">Shipped</th>
                <th className="text-right px-3 py-2">Delivered</th>
                <th className="text-right px-3 py-2">Avg transit</th>
                <th className="text-right px-3 py-2">Stuck &gt; 14d</th>
                <th className="text-right px-3 py-2">Returned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {delivery.byCountry.map((c) => (
                <tr key={c.country}>
                  <td className="px-3 py-2 font-medium">{c.country}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(c.shipped)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtNum(c.delivered)}
                    {c.shipped > 0 && <span className="text-gray-400 text-xs"> ({Math.round((c.delivered / c.shipped) * 100)}%)</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.avg_transit_days != null ? `${c.avg_transit_days}d` : '—'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${c.stuck_gt_14d > 0 ? 'text-rose-700 font-semibold' : 'text-gray-400'}`}>
                    {c.stuck_gt_14d > 0 ? fmtNum(c.stuck_gt_14d) : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${c.returned > 0 ? 'text-rose-700 font-semibold' : 'text-gray-400'}`}>
                    {c.returned > 0 ? fmtNum(c.returned) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function OwnerAnalytics() {
  const [preset, setPreset] = useState('30d');
  const [from, setFrom] = useState(daysAgoIso(29));
  const [to, setTo] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  async function load(f = from, t = to) {
    setLoading(true);
    try {
      const res = await cockpitApi.analytics({ from: f, to: t });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load analytics');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function applyPreset(p) {
    const { from: f, to: t } = p.range();
    setPreset(p.key); setFrom(f); setTo(t);
    load(f, t);
  }

  function applyCustom(f, t) {
    setPreset('custom'); setFrom(f); setTo(t);
    if (f && t && f <= t) load(f, t);
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500">
            Order analysis between any two dates. View-only.
          </p>
        </div>
        <button onClick={() => load()} disabled={loading}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2 mt-4">
        {PRESETS.map((p) => (
          <button key={p.key} onClick={() => applyPreset(p)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                    preset === p.key
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}>
            {p.label}
          </button>
        ))}
        <div className="flex items-end gap-2 ml-2">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">From</span>
            <input type="date" value={from} max={to}
                   onChange={(e) => applyCustom(e.target.value, to)}
                   className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">To</span>
            <input type="date" value={to} min={from} max={todayIso()}
                   onChange={(e) => applyCustom(from, e.target.value)}
                   className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </label>
        </div>
      </div>

      {!data && loading && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {data && (
        <>
          <Funnel funnel={data.funnel} avgDays={data.avgDays} />
          <TrendChart trend={data.trend} />
          {data.vendorPerf && <VendorTable vendorPerf={data.vendorPerf} />}
          {data.delivery && <DeliverySection delivery={data.delivery} />}
          {data.exceptions && <ExceptionsSection exceptions={data.exceptions} />}
          {data.productPerf && <ProductTable productPerf={data.productPerf} />}
        </>
      )}
    </div>
  );
}
